'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import { Search, Plus, AlertTriangle, ShieldCheck, Settings } from 'lucide-react'
import { db, StockItem as DbStock } from '../lib/db'

export function SettingsPage() {
  const [stock, setStock] = useState<DbStock[]>(() => db.getStock())
  const [search, setSearch] = useState('')
  const [showNew, setShowNew] = useState(false)

  // Creation fields
  const [name, setName] = useState('')
  const [qty, setQty] = useState(10)
  const [minQty, setMinQty] = useState(5)
  const [unit, setUnit] = useState('unidades')

  // General settings state
  const [settings, setSettings] = useState(() => db.getSettings())
  const [isSaved, setIsSaved] = useState(false)
  const [spaName, setSpaName] = useState(settings.spaName)
  const [timezone, setTimezone] = useState(settings.timezone)
  const [currency, setCurrency] = useState(settings.currency)
  const [openTime, setOpenTime] = useState(settings.openTime)
  const [closeTime, setCloseTime] = useState(settings.closeTime)
  const [cancellationPolicy, setCancellationPolicy] = useState(settings.cancellationPolicy)
  const [warrantyType, setWarrantyType] = useState<any>(settings.warrantyType || 'percent')
  const [warrantyVal, setWarrantyVal] = useState(settings.warrantyVal || 20)
  const [birthdayMessageLeadDays, setBirthdayMessageLeadDays] = useState(settings.birthdayMessageLeadDays || 0)
  const [birthdayBenefit, setBirthdayBenefit] = useState(settings.birthdayBenefit || '15% de Descuento')
  const [inactiveThresholdDays, setInactiveThresholdDays] = useState(settings.inactiveThresholdDays || 45)

  const filteredStock = useMemo(() => {
    if (!search) return stock
    return stock.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
  }, [stock, search])

  const handleCreateStock = () => {
    if (!name) return
    const newItem: DbStock = {
      id: `stk-${Date.now()}`,
      name,
      qty,
      minQty,
      unit
    }
    const updated = [...stock, newItem]
    setStock(updated)
    db.saveStock(updated)
    setShowNew(false)

    // Trigger notification if low stock
    if (qty < minQty) {
      const notifications = db.getNotifications()
      const newNotif = {
        id: `not-${Date.now()}`,
        text: `Alerta: Stock bajo de ${name} (${qty} de ${minQty} mín)`,
        time: 'Hace un momento',
        unread: true
      }
      db.saveNotifications([newNotif, ...notifications])
    }

    setName('')
    setQty(10)
    setMinQty(5)
    setUnit('unidades')
  }

  const handleSaveSettings = () => {
    const updated = {
      ...settings,
      spaName,
      timezone,
      currency,
      openTime,
      closeTime,
      cancellationPolicy,
      warrantyType,
      warrantyVal,
      birthdayMessageLeadDays,
      birthdayBenefit,
      inactiveThresholdDays
    }
    setSettings(updated)
    db.saveSettings(updated)
    setIsSaved(true)
    setTimeout(() => setIsSaved(false), 3000)
  }

  const adjustQty = (id: string, delta: number) => {
    const updated = stock.map(s => {
      if (s.id === id) {
        const newQty = Math.max(0, s.qty + delta)
        // Trigger alert if crosses limit
        if (newQty < s.minQty && s.qty >= s.minQty) {
          const notifications = db.getNotifications()
          const newNotif = {
            id: `not-${Date.now()}`,
            text: `Alerta de Insumos: ${s.name} llegó a nivel crítico (${newQty} ${s.unit})`,
            time: 'Hace un momento',
            unread: true
          }
          db.saveNotifications([newNotif, ...notifications])
        }
        return { ...s, qty: newQty }
      }
      return s
    })
    setStock(updated)
    db.saveStock(updated)
  }

  const inp = 'w-full h-9 border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-400/40'

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Ajustes & Control de Stock</h1>
          <p className="text-xs text-slate-500">Configuración global del centro y niveles críticos de insumos de cabina</p>
        </div>
        <button onClick={handleSaveSettings}
          className="flex items-center gap-1.5 px-3.5 h-9 text-sm font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
          Guardar Cambios
        </button>
      </div>

      <div className="flex-1 overflow-auto space-y-4 pr-1">
        {isSaved && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-800 text-xs font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Configuraciones generales guardadas exitosamente.
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* General Config form */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm space-y-3">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider pb-2 border-b border-slate-100 flex items-center gap-1.5">
                <Settings className="h-4 w-4 text-cyan-600" />
                Perfil & Agenda del Centro
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Nombre Comercial</label>
                  <input type="text" className={inp} value={spaName} onChange={e => setSpaName(e.target.value)} />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Zona Horaria</label>
                  <select className={inp} value={timezone} onChange={e => setTimezone(e.target.value)}>
                    <option value="America/Santiago">America/Santiago (CLT)</option>
                    <option value="America/Bogota">America/Bogota (COT)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Hora Apertura</label>
                  <input type="time" className={inp} value={openTime} onChange={e => setOpenTime(e.target.value)} />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Hora Cierre</label>
                  <input type="time" className={inp} value={closeTime} onChange={e => setCloseTime(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Campaign timing details */}
            <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm space-y-3">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider pb-2 border-b border-slate-100">
                Abono & Alertas Automatizadas
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Garantía Webpay</label>
                  <select className={inp} value={warrantyType} onChange={e => setWarrantyType(e.target.value as any)}>
                    <option value="percent">Porcentaje de reserva (%)</option>
                    <option value="fixed">Monto Fijo de garantía ($)</option>
                    <option value="none">Sin abono previo</option>
                  </select>
                </div>
                {warrantyType !== 'none' && (
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Valor de la Garantía</label>
                    <input type="number" className={inp} value={warrantyVal} onChange={e => setWarrantyVal(Number(e.target.value))} />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Anticipación Alerta Cumpleaños</label>
                  <select className={inp} value={birthdayMessageLeadDays} onChange={e => setBirthdayMessageLeadDays(Number(e.target.value))}>
                    <option value={0}>El mismo día</option>
                    <option value={3}>3 días antes</option>
                    <option value={7}>7 días antes</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Beneficio Cupón Regalo</label>
                  <input type="text" className={inp} value={birthdayBenefit} onChange={e => setBirthdayBenefit(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500 mb-1 block">Límite Inactividad (Días)</label>
                <input type="number" className={inp} value={inactiveThresholdDays} onChange={e => setInactiveThresholdDays(Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Stock inventory side (#6) */}
          <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100 mb-3">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Inventario e Insumos Críticos de Cabina
                </h3>
                <button onClick={() => setShowNew(true)} className="h-7 px-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-sm">
                  <Plus className="h-3 w-3" /> Registrar Insumo
                </button>
              </div>

              <div className="relative mb-3">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input type="text" placeholder="Buscar insumos..." className="w-full h-8 pl-8 pr-3 border border-slate-200 rounded-lg text-xs bg-slate-50" value={search} onChange={e => setSearch(e.target.value)} />
              </div>

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredStock.map(item => {
                  const isLow = item.qty < item.minQty
                  return (
                    <div key={item.id} className={cn("p-3 border rounded-xl flex items-center justify-between shadow-sm transition", isLow ? "border-amber-200 bg-amber-50/30" : "border-slate-100 bg-white")}>
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">{item.name}</span>
                        <span className="text-[10px] text-slate-400">Mínimo Crítico: {item.minQty} {item.unit}</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {isLow && (
                          <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1 animate-pulse">
                            Stock Bajo
                          </span>
                        )}
                        <span className="text-xs font-extrabold text-slate-700">{item.qty} {item.unit}</span>
                        <div className="flex gap-1">
                          <button onClick={() => adjustQty(item.id, -1)} className="h-6 w-6 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-xs flex items-center justify-center">-</button>
                          <button onClick={() => adjustQty(item.id, 1)} className="h-6 w-6 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-xs flex items-center justify-center">+</button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 mb-4">Registrar Nuevo Insumo</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre del Insumo *</label>
                <input type="text" placeholder="Ej: Gel Descongestionador" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Stock Actual *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={qty} onChange={e => setQty(Number(e.target.value))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Mínimo Alerta *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={minQty} onChange={e => setMinQty(Number(e.target.value))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Unidad de Medida *</label>
                <input type="text" placeholder="Ej: unidades, litros, potes" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={unit} onChange={e => setUnit(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreateStock} disabled={!name} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Guardar Insumo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
